import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

// Brand DNA Chat-Agent v1. Edits ONLY three fields (positioning, usp, traits)
// via the backend, which enforces the whitelist. This component only relays the
// backend's decision and NEVER fabricates a success message:
//   reject  -> honest refusal (out of scope)
//   clarify -> question, nothing written
//   options -> vague free-text wish: pick one of 2-3 variants, then it writes
//   preview -> concrete change proposed, apply on confirm
//   done    -> already written; reply + value come from the DB read-back
// UI language follows the app language toggle (useLanguage → lang).

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Dark app palette (matches Settings.js / DashboardLayout).
const C = {
  card: '#1A2B42', border: '#2A3B52', page: '#0A1628',
  text: '#FFFFFF', accent: '#00D9C8', muted: '#A0AEC0', userBubble: '#00D9C8',
};
const font = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

const s = {
  card: { background: C.card, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', fontFamily: font, color: C.text },
  head: { fontSize: 16, fontWeight: 700, margin: '0 0 2px' },
  sub: { fontSize: 12, color: C.muted, margin: '0 0 14px' },
  log: { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 360, overflowY: 'auto', marginBottom: 12 },
  row: (mine) => ({ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }),
  bubble: (mine) => ({
    maxWidth: '86%', fontSize: 13.5, lineHeight: 1.45, padding: '9px 13px', borderRadius: 12,
    background: mine ? C.userBubble : C.page, color: mine ? '#0A1628' : C.text,
    border: mine ? 'none' : `0.5px solid ${C.border}`,
  }),
  box: { marginTop: 8, padding: '10px 12px', border: `0.5px solid ${C.border}`, borderRadius: 10, background: C.page },
  boxLabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: C.accent, textTransform: 'uppercase' },
  old: { fontSize: 12.5, color: C.muted, textDecoration: 'line-through', margin: '5px 0 0' },
  neu: { fontSize: 13.5, color: C.text, margin: '3px 0 0', fontWeight: 600 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  chip: { fontSize: 12, padding: '3px 9px', borderRadius: 999, border: `0.5px solid ${C.border}`, background: C.page },
  variant: (busy) => ({
    display: 'block', width: '100%', textAlign: 'left', fontSize: 13, lineHeight: 1.4,
    padding: '9px 11px', marginTop: 7, borderRadius: 8, cursor: busy ? 'default' : 'pointer',
    border: `0.5px solid ${C.border}`, background: C.page, color: C.text,
  }),
  btnRow: { display: 'flex', gap: 8, marginTop: 10 },
  btn: (primary) => ({
    fontSize: 12.5, fontWeight: 600, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
    border: `0.5px solid ${primary ? C.accent : C.border}`,
    background: primary ? C.accent : C.page, color: primary ? '#0A1628' : C.text,
  }),
  form: { display: 'flex', gap: 8 },
  input: { flex: 1, fontSize: 13.5, fontFamily: font, padding: '9px 12px', borderRadius: 8, border: `0.5px solid ${C.border}`, background: C.page, color: C.text, outline: 'none' },
  send: { fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: 'none', background: C.accent, color: '#0A1628', cursor: 'pointer' },
};

const T = {
  de: {
    head: 'Per Chat ändern',
    sub: 'Ich kann Positionierung, Alleinstellung und Charakter-Eigenschaften anpassen. Farben, Logo, Bilder, Zielgruppen und Beispiel-Posts sind hier noch nicht änderbar.',
    apply: 'Übernehmen', cancel: 'Abbrechen', undo: 'Rückgängig', redo: 'Wiederherstellen',
    preview: 'Vorschau', updated: 'gespeichert', reverted: 'zurückgesetzt',
    cancelled: '(abgebrochen)', send: 'Senden',
    placeholder: 'z. B. „klinge nahbarer" oder „setze die Positionierung auf …"',
    unavailable: 'Der Assistent ist gerade nicht erreichbar. Bitte erneut versuchen.',
    failed: 'Diese Änderung konnte nicht angewendet werden.',
  },
  en: {
    head: 'Edit with chat',
    sub: "I can update positioning, what sets you apart, and character traits. Colors, logo, images, audiences and example posts aren't editable here yet.",
    apply: 'Apply', cancel: 'Cancel', undo: 'Undo', redo: 'Restore',
    preview: 'preview', updated: 'saved', reverted: 'reverted',
    cancelled: '(cancelled)', send: 'Send',
    placeholder: 'e.g. "sound more approachable" or "set positioning to …"',
    unavailable: 'The assistant is unavailable right now. Please try again.',
    failed: "That change couldn't be applied.",
  },
};

// One change = one card with a clear state:
//   preview  -> proposed, not written; [Apply][Cancel]
//   applied  -> written to DB (display_value = read-back); [Undo]
//   reverted -> original restored in DB (display_value = read-back); [Restore]
// Undo/Restore toggle the SAME card between the stable base_value and
// applied_value anchors — no new chat messages, no drifting values.
function ChangeCard({ change, tr, onConfirm, onCancel, onUndo, onRedo, busy }) {
  const { state, base_value, display_value } = change;
  const stateWord = state === 'applied' ? tr.updated : state === 'reverted' ? tr.reverted : tr.preview;
  return (
    <div style={s.box}>
      <div style={s.boxLabel}>{change.label} · {stateWord}</div>
      {change.is_list ? (
        <div style={s.chips}>
          {(display_value || []).map((t, i) => <span key={i} style={s.chip}>{t}</span>)}
        </div>
      ) : (
        <>
          {/* show the value being replaced (struck) only while proposing/applied */}
          {state !== 'reverted' && base_value ? <p style={s.old}>{base_value}</p> : null}
          <p style={s.neu}>{display_value}</p>
        </>
      )}
      <div style={s.btnRow}>
        {state === 'preview' && <button style={s.btn(true)} disabled={busy} onClick={onConfirm}>{tr.apply}</button>}
        {state === 'preview' && <button style={s.btn(false)} disabled={busy} onClick={onCancel}>{tr.cancel}</button>}
        {state === 'applied' && <button style={s.btn(false)} disabled={busy} onClick={onUndo}>{tr.undo}</button>}
        {state === 'reverted' && <button style={s.btn(false)} disabled={busy} onClick={onRedo}>{tr.redo}</button>}
      </div>
    </div>
  );
}

function OptionsCard({ options, tr, onPick, busy, chosen }) {
  return (
    <div style={s.box}>
      <div style={s.boxLabel}>{options[0]?.label}</div>
      {options.map((o, i) => (
        <button key={i} style={s.variant(busy)} disabled={busy || chosen} onClick={() => onPick(o)}>
          {o.value}
        </button>
      ))}
    </div>
  );
}

export default function BrandDnaChat({ onChanged }) {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const tr = T[lang] || T.de;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef(null);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [messages]);

  const push = (m) => setMessages((prev) => [...prev, m]);

  // Normalize a backend change {field_key,label,old_value,new_value} into a
  // stable card with immutable base/applied anchors (so Undo/Restore never drift).
  const toCard = (c, state) => ({
    field_key: c.field_key,
    label: c.label,
    is_list: Array.isArray(c.new_value),
    state,
    base_value: c.old_value,        // original value before the change
    applied_value: c.new_value,     // value the change sets
    display_value: c.new_value,     // what's currently shown (= DB read-back)
  });

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    push({ role: 'user', text });
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/brand-dna/chat`,
        { message: text, language: lang }, { headers: getAuthHeader() });
      const { status, reply, change, options } = r.data;
      if (status === 'done') {
        // Immediate change already written. The CARD is the full confirmation —
        // no separate "saved" text line (Fix 1).
        push({ role: 'assistant', text: '', change: toCard(change, 'applied') });
        onChanged?.();
      } else if (status === 'preview') {
        push({ role: 'assistant', text: reply, change: toCard(change, 'preview') });
      } else if (status === 'options') {
        push({ role: 'assistant', text: reply, options });
      } else {
        push({ role: 'assistant', text: reply }); // reject / clarify
      }
    } catch (err) {
      push({ role: 'assistant', text: tr.unavailable });
    } finally {
      setBusy(false);
    }
  };

  // First write: confirm a preview OR pick a variant. Replaces the message with
  // an APPLIED card and clears its text — the card alone confirms (Fix 1). No
  // extra "saved" chat line is pushed.
  const applyInitial = async (msgIdx, fieldKey, value) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/brand-dna/chat/apply`,
        { field_key: fieldKey, new_value: value, language: lang }, { headers: getAuthHeader() });
      const card = toCard(r.data.change, 'applied');
      setMessages((prev) => prev.map((m, i) => i === msgIdx ? { ...m, text: '', options: null, change: card } : m));
      onChanged?.();
    } catch (err) {
      push({ role: 'assistant', text: err?.response?.data?.detail || tr.failed });
    } finally {
      setBusy(false);
    }
  };

  // Undo/Restore: toggle the SAME card between its stable anchors. Writes the
  // target value to the DB and shows the READ-BACK — no new chat message, so no
  // endless flip-flop (Fix 2).
  const transition = async (msgIdx, toState) => {
    if (busy) return;
    const ch = messages[msgIdx]?.change;
    if (!ch) return;
    const target = toState === 'reverted' ? ch.base_value : ch.applied_value;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/brand-dna/chat/apply`,
        { field_key: ch.field_key, new_value: target, language: lang }, { headers: getAuthHeader() });
      const readback = r.data.change.new_value; // DB read-back proves the write landed
      setMessages((prev) => prev.map((m, i) =>
        i === msgIdx ? { ...m, change: { ...ch, state: toState, display_value: readback } } : m));
      onChanged?.();
    } catch (err) {
      push({ role: 'assistant', text: err?.response?.data?.detail || tr.failed });
    } finally {
      setBusy(false);
    }
  };

  const cancel = (msgIdx) => {
    setMessages((prev) => prev.map((m, i) => i === msgIdx ? { ...m, change: null, text: `${m.text} ${tr.cancelled}` } : m));
  };

  return (
    <div style={s.card}>
      <h3 style={s.head}>{tr.head}</h3>
      <p style={s.sub}>{tr.sub}</p>

      {messages.length > 0 && (
        <div style={s.log} ref={logRef}>
          {messages.map((m, i) => (
            <div key={i}>
              {m.text ? (
                <div style={s.row(m.role === 'user')}>
                  <div style={s.bubble(m.role === 'user')}>{m.text}</div>
                </div>
              ) : null}
              {m.change && (
                <ChangeCard
                  change={m.change} tr={tr} busy={busy}
                  onConfirm={() => applyInitial(i, m.change.field_key, m.change.applied_value)}
                  onCancel={() => cancel(i)}
                  onUndo={() => transition(i, 'reverted')}
                  onRedo={() => transition(i, 'applied')}
                />
              )}
              {m.options && (
                <OptionsCard
                  options={m.options} tr={tr} busy={busy} chosen={m.chosen}
                  onPick={(o) => applyInitial(i, o.field_key, o.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <form style={s.form} onSubmit={send}>
        <input
          style={s.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={tr.placeholder}
          disabled={busy}
        />
        <button type="submit" style={s.send} disabled={busy}>{tr.send}</button>
      </form>
    </div>
  );
}
